import {
  CreatePaymentHistoryRequestSchema,
  CreatePaymentScheduleRequestSchema,
  CreateSubscriptionRequestSchema,
  GenerarPlanRequestSchema,
  PaymentHistoryListQuerySchema,
  PaymentHistorySchema,
  PaymentScheduleListQuerySchema,
  PaymentScheduleSchema,
  SubscriptionListQuerySchema,
  SubscriptionSchema,
  SuscripcionesDashboardSchema,
  UpdatePaymentHistoryRequestSchema,
  UpdateSubscriptionRequestSchema,
} from '@visep/shared-types/suscripciones';
import type {
  CreatePaymentScheduleRequest,
  GenerarPlanRequest,
  PaymentSchedule,
  PaymentScheduleListQuery,
  SuscripcionesDashboard,
} from '@visep/shared-types/suscripciones';
import type { MetodoPago } from '@visep/shared-types/payment-method';
import type { Helpers, ListResult } from './internal.js';
import { listResultSchema, makeCrudResource, toQuery } from './internal.js';

export function createSuscripcionesResource({ request }: Helpers) {
  const subscriptions = makeCrudResource(request, {
    basePath: '/api/suscripciones',
    schema: SubscriptionSchema,
    listQuerySchema: SubscriptionListQuerySchema,
    createSchema: CreateSubscriptionRequestSchema,
    updateSchema: UpdateSubscriptionRequestSchema,
  });
  const pagos = makeCrudResource(request, {
    basePath: '/api/suscripciones-pagos',
    schema: PaymentHistorySchema,
    listQuerySchema: PaymentHistoryListQuerySchema,
    createSchema: CreatePaymentHistoryRequestSchema,
    updateSchema: UpdatePaymentHistoryRequestSchema,
  });
  return {
    listSubscriptions: subscriptions.list,
    async getDashboard(): Promise<SuscripcionesDashboard> {
      return SuscripcionesDashboardSchema.parse(await request('GET', '/api/suscripciones/dashboard'));
    },
    getSubscription: subscriptions.get,
    createSubscription: subscriptions.create,
    updateSubscription: subscriptions.update,
    deleteSubscription: subscriptions.delete,
    listPagos: pagos.list,
    createPago: pagos.create,
    getPago: pagos.get,
    updatePago: pagos.update,
    deletePago: pagos.delete,
    async listCronograma(query?: PaymentScheduleListQuery): Promise<ListResult<PaymentSchedule>> {
      const parsed = PaymentScheduleListQuerySchema.parse(query ?? {});
      return listResultSchema(PaymentScheduleSchema).parse(await request('GET', `/api/suscripciones-cronograma${toQuery(parsed)}`));
    },
    async createCronograma(input: CreatePaymentScheduleRequest): Promise<PaymentSchedule> {
      return PaymentScheduleSchema.parse(await request('POST', '/api/suscripciones-cronograma', CreatePaymentScheduleRequestSchema.parse(input)));
    },
    async cobrarCuota(id: string, metodoPago?: MetodoPago): Promise<PaymentSchedule> {
      return PaymentScheduleSchema.parse(
        await request(
          'POST',
          `/api/suscripciones-cronograma/${encodeURIComponent(id)}/cobrar`,
          metodoPago ? { metodoPago } : undefined,
        ),
      );
    },
    async deleteCronograma(id: string): Promise<void> {
      await request('DELETE', `/api/suscripciones-cronograma/${encodeURIComponent(id)}`);
    },
    async generarCronograma(id: string): Promise<{ created: number }> {
      return (await request('POST', `/api/suscripciones/${encodeURIComponent(id)}/generar-cronograma`)) as {
        created: number;
      };
    },
    async generarPlan(id: string, input: GenerarPlanRequest): Promise<{ created: number }> {
      return (await request(
        'POST',
        `/api/suscripciones/${encodeURIComponent(id)}/generar-plan`,
        GenerarPlanRequestSchema.parse(input),
      )) as { created: number };
    },
  };
}
